import _ from 'lodash'
import {
  getFileExtension,
  removeFileExtension,
  replaceJpegToJpg,
} from '@com/utils/file'
import { getBinUuidFromFiles } from '@ps/bridge/qc/bin'
import { parseBinContent, parseComponent } from '@ps/utils/game'
import { Base64 } from 'js-base64'
import { saveFile } from '@ps/api/editor/file'
import { AssetRoute } from '@ps/utils/config'
import qc from 'qc'
import { addImageNode } from '@ps/bridge/qc/node/image'
import { createImageComponent } from '@ps/bridge/qc/bin/image'
import { libDataPool } from '@ps/bridge/resource/pool/index'

/**
 * 拼接文件名字
 * @returns {`${name}_${uuid}.${extension}`}
 */
export function splicingFileName({
  name,
  uuid,
}: {
  name: string
  uuid?: string
}): string {
  const extension = getFileExtension(name)
  const newName = removeFileExtension(name)
  return uuid ? `${newName}_${uuid}.${extension}` : `${newName}.${extension}`
}

/**
 * 添加资源到素材包
 */
async function addImageToPlayable(params: {
  name: string
  pid: number
  files: {
    name: string
    data: string
  }[]
  padding: any
  resourcePath: string
}) {
  const { files, name, pid, padding, resourcePath } = params
  const pathPrefix = resourcePath
  const binUUID = getBinUuidFromFiles({
    files,
    binFileName: name + '.bin',
  })

  if (binUUID === '') {
    throw new Error('组件里没有找到.bin文件')
  }

  const promises = files.map((item) => {
    // 上传图片节点时不需要添加 uuid 到图片名字上
    const finalName = splicingFileName({
      name: item.name,
    })
    const path = pathPrefix + finalName

    if (finalName.indexOf('bin') !== -1 && padding) {
      const fileData = parseBinContent(Base64.decode(item.data))
      fileData.sourceMeta.padding = {
        0: JSON.parse(padding).nine,
      }
      fileData.data[0] = JSON.stringify(fileData.sourceMeta)
      return saveFile(
        {
          pid: Number(pid),
          path,
        },
        JSON.stringify(fileData.data),
      )
    }

    return saveFile(
      {
        pid: Number(pid),
        path,
        enc: 'base64',
      },
      item.data,
    )
  })

  // 上传组件的文件
  await Promise.all(promises)
}

async function addImageAndNodeToPlayable(params: {
  name: string
  pid: number
  files: {
    name: string
    data: string
  }[]
  nodeName: string
  sceneNode: qc.Node
  padding: string
  resourcePath: string
}): Promise<qc.Node> {
  if (_.isEmpty(params.sceneNode)) {
    throw new Error('sceneNode 不能为空')
  }

  const { pid, sceneNode, nodeName, name, padding, resourcePath } = params
  const files = params.files.map((file) => {
    return {
      ...file,
      // 由于引擎不支持 jpeg 后缀，所以需要将 jpeg 改为 jpg
      name: replaceJpegToJpg(file.name),
    }
  })

  // 添加资源
  await addImageToPlayable({
    files,
    name,
    pid,
    padding,
    resourcePath,
  })
  // 记录到资源映射池，方便后续使用
  const imagePath = files.filter((item) => item.name.indexOf('bin') > 0)[0]
    ?.name
  libDataPool.setLibData(`${resourcePath}${imagePath}`, {
    name: nodeName,
  })
  return addImageNode({
    files,
    name,
    scene: sceneNode,
    padding,
    nodeName,
    pid,
    resourcePath,
  })
}

/**
 * 添加图片到场景中
 */
export async function addImage({
  file,
  fileName,
  pid,
  sceneNode,
  padding,
}: {
  file: File
  fileName: string
  pid: number
  sceneNode: qc.Node
  padding: string
}) {
  const resourcePath = AssetRoute.GAME_ROOT
  const newComponent = await createImageComponent({
    file,
    name: fileName,
    resourcePath,
  })
  const component: any = parseComponent({
    data: newComponent,
  })
  if (component.errorMsg) {
    throw new Error(component.errorMsg)
  }
  return addImageAndNodeToPlayable({
    name: component.prefabName,
    pid,
    files: component.files,
    nodeName: removeFileExtension(fileName),
    sceneNode,
    padding,
    resourcePath,
  })
}

/**
 * 更新指定节点的图片
 */
export async function updateImage() {
  //
}
